import React from 'react';
import { Modal, Form, Input, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import './UpdateEmailModal.scss';

interface UpdateEmailModalProps {
  visible: boolean;
  currentEmail?: string;
  isSubmitting: boolean;
  onClose: () => void;
  onSuccess: (newEmail: string) => void;
}

const UpdateEmailModal: React.FC<UpdateEmailModalProps> = ({
  visible,
  currentEmail = '',
  isSubmitting,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('email');
  const [form] = Form.useForm();
  const emailValue = Form.useWatch('email', form);

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = (values: { email: string }) => {
    onSuccess(values.email);
  };

  const handleValuesChange = (changedValues: any) => {
    const fieldNames = Object.keys(changedValues);
    if (fieldNames.length > 0) {
      form.setFields(
        fieldNames.map((name) => ({
          name: name as any,
          errors: [],
        }))
      );
    }
  };

  const isUpdateDisabled =
    isSubmitting ||
    !emailValue ||
    emailValue.trim() === currentEmail.trim();

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      footer={null}
      centered
      className="update-email-modal"
    >
      <div className="modal-title">{t('updateEmailModal.title')}</div>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ email: currentEmail }}
        onFinish={handleSubmit}
        validateTrigger="onSubmit"
        onValuesChange={handleValuesChange}
      >
        <Form.Item
          name="email"
          label={t('updateEmailModal.emailLabel')}
          rules={[
            {
              required: true,
              message: t('emailRequired'),
            },
            {
              type: 'email',
              message: t('emailInvalid'),
            },
          ]}
        >
          <Input placeholder={t('emailPlaceholder')} className="email-input" />
        </Form.Item>
      </Form>

      <div className="modal-footer">
        <Button onClick={handleCancel} disabled={isSubmitting} block>
          {t('updateEmailModal.cancelButton')}
        </Button>
        <Button
          type="primary"
          onClick={() => form.submit()}
          loading={isSubmitting}
          disabled={isUpdateDisabled}
          block
        >
          {t('updateEmailModal.updateButton')}
        </Button>
      </div>
    </Modal>
  );
};

export default UpdateEmailModal;